import { Session } from 'next-auth';
import { signOut, useSession } from 'next-auth/react';
import Router, { useRouter } from 'next/router';
import React, { PropsWithChildren, ReactNode, useEffect } from 'react';
import { setToken } from '../../apis/grispiApi';
import { redirectTo } from '../../utils/routeHelper';
import { StorageHelperKeys, getLocalStorageItem, setLocalStorageItem } from '../../utils/storageHelpers';

function redirectOnUnauthenticated(redirectUrl?: string) {
    if (Router.asPath !== '/login-page') {
        const signOutFlag = getLocalStorageItem(StorageHelperKeys.USER_LOGOUT);
        if (signOutFlag) {
            setLocalStorageItem(StorageHelperKeys.USER_LOGOUT, false);
            redirectTo('/login-page');
            return;
        }
        return redirectTo(
            `/login-page?serverMessage=sessionExpired${redirectUrl ? `&callBackUrl=${redirectUrl}` : ''}`
        );
    }
}
async function redirectOnSessionError() {
    await signOut({ redirect: false });
    console.debug(`redirectOnSessionError`, 'session terminated, should redirect to login page now');
    // await redirectOnUnauthenticated();
}

const sendingDoneFn = (e) => alert(e.detail?.message || 'Bir hata oluştu'); //FIXME show a proper ant design message
function useULAK(session: Session) {
    useEffect(() => {
        try {
            // interceptConsole();
            window.addEventListener('BugReportButton:SendingDone', sendingDoneFn);
        } catch (error) {
            // HACK: cannot add ErrorBoundary to _app.tsx due to ant-design limitation, try with ant-design v5
            redirectTo('/500');
        }
        return () => window.removeEventListener('BugReportButton:SendingDone', sendingDoneFn);
    }, []);
    useEffect(() => {
        session?.accessToken &&
            window.dispatchEvent(new CustomEvent('ULAK_AUTH_TOKEN_READY', { detail: session.accessToken }));
    }, [session]);
}

/**
 * If the user is not authenticated, redirect to the login page. If the user is authenticated, set the access token in the axios configuration
 * @param  - `children` is the content that will be rendered if the user is authenticated
 * @param  - `loadingLayout` is a component (already attached to a page component, see pages folder) that will be rendered till the authentication is checked
 */
export default function AuthenticationGate({
    children,
    loadingLayout
}: PropsWithChildren<{ loadingLayout: ReactNode }>) {
    const { asPath } = useRouter();
    // if `{ required: true }` is supplied, `status` can only be "loading" or "authenticated"
    const { data: session, status } = useSession({
        required: true,
        onUnauthenticated: () => redirectOnUnauthenticated(asPath)
    });

    useULAK(session);

    const redirectNeeded =
        session?.error === 'RefreshAccessTokenError' || (status === 'authenticated' && !session?.accessToken);
    useEffect(() => {
        redirectNeeded && redirectOnSessionError();
    }, [redirectNeeded]);

    if (status === 'loading' || redirectNeeded)
        return React.isValidElement(loadingLayout) ? <>{loadingLayout}</> : null;

    // github.com/nextauthjs/next-auth/discussions/3550#discussioncomment-3318753
    if (status === 'authenticated' && session?.accessToken) {
        setToken(session.accessToken);
    }

    return <>{children}</>;
}

export { redirectOnSessionError };
